import {
  FC,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { AppCtx } from "src/contexts/app.context";
import {
  HomePageSingleBrand,
  ImageBannerData,
  SummaryBrand,
} from "src/types/graphql.d";
import { CarouselHeader } from "src/molecules/CarouselHeader";
import { Carousel } from "src/molecules/Carousel";
import { BrandCard } from "src/molecules/BrandCard";
import { Box, BoxProps, Flex, useBreakpoints } from "@otrium/core";
import { EmotionBaseProps as BaseProps } from "src/types/global";
import { useImageHeight } from "src/hooks/useImageHeight";
import SegmentBrandTracking from "src/atoms/SegmentTracking/SegmentBrandTracking";
import {
  Brand,
  BrandInjectedProps,
  SEGMENT_POSITIONS,
  SEGMENT_PROMOTION_TYPE,
} from "src/segment";
import { filterExpiredImageBannerDeals } from "src/utils/filterExpiredImageBannerDeals";
import { LoginRegisterModal } from "../LoginRegisterModal";
import {
  GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
  GATED_POPUP_REDIRECT_DATA,
} from "src/constants/gatedItem";
import {
  removeStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import { useIsClient } from "src/hooks/use-is-client";
import { Skeleton } from "src/atoms/Skeleton";
import { theme } from "src/theme";

interface BrandCarouselProps {
  title?: string;
  subTitle?: string;
  headerLinkHref?: string;
  headerLinkAs?: string;
  brands: Brand[] | null;
  genderId: string;
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  orderByNew?: boolean;
  associatedCategoryId?: string | null;
  disableTranslation?: boolean;
  slideShadowSpace?: number;
  shouldReset?: boolean;
  tab?: string;
  hasVerticalPadding?: boolean;
  promotionType?: SEGMENT_PROMOTION_TYPE;
  pageName?: string;
  isGated?: boolean;
  shopType?: string;
  setHasTimerEnded?: (hasTimerEnded: boolean) => void;
}

const BrandCarousel: FC<BoxProps & BaseProps & BrandCarouselProps> = ({
  title,
  subTitle,
  headerLinkHref,
  headerLinkAs,
  brands,
  genderId,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  shouldReset,
  orderByNew = false,
  associatedCategoryId,
  disableTranslation = false,
  slideShadowSpace = 48,
  tab,
  hasVerticalPadding = true,
  promotionType,
  pageName,
  isGated,
  shopType,
  setHasTimerEnded,
  ...rest
}) => {
  const { locale } = useContext(AppCtx);
  const { isMobile } = useBreakpoints();
  const [open, setOpen] = useState<boolean>(false);
  const [imageElementWrapperHeight, getImageElementWrapper] = useImageHeight();
  const isClient = useIsClient();

  const filteredBrands = useMemo(
    () => filterExpiredImageBannerDeals(brands as ImageBannerData[]),
    [brands]
  );
  const [brandsData, setBrandsData] = useState<Brand[]>(filteredBrands);

  const getPosition = (brand: Brand, index: number) => {
    const { __typename: typeName } = brand;
    const isImageBanner = typeName === "ImageBannerData";

    return `${
      isImageBanner ? SEGMENT_POSITIONS.curated : SEGMENT_POSITIONS.carousel
    }-${title || ("title" in brand ? brand.title : "")}-${index + 1}`;
  };

  useEffect(() => {
    const allBrands =
      filteredBrands.length > 3 &&
      promotionType === SEGMENT_PROMOTION_TYPE.imageBanners
        ? filteredBrands.slice(0, 3)
        : filteredBrands || [];

    setBrandsData(allBrands);
  }, [filteredBrands, promotionType]);

  const handleTimerExpired = useCallback(
    (uuid: string) => {
      const newBrandsData =
        filterExpiredImageBannerDeals(brands as ImageBannerData[]).filter(
          (item) => item?.block_uuid !== uuid
        ) || [];

      setBrandsData(
        newBrandsData.length > 3
          ? newBrandsData.slice(0, 3)
          : newBrandsData.length < 3
          ? []
          : newBrandsData
      );
    },
    [brands]
  );

  if (
    promotionType === SEGMENT_PROMOTION_TYPE.imageBanners &&
    brandsData.length < 3
  ) {
    return null;
  }

  const handleViewAllClick = () => {
    if (isGated) {
      setOpen(true);
    }
  };

  const setRedirectData = (type: string) => {
    removeStorageItem(GATED_POPUP_REDIRECT_DATA);
    setStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      shopTypeFromRedirect: shopType,
      promo_type: `${promotionType as string}_${pageName as string}`,
      actionSource: GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
    });
  };

  return (
    <Box as="section" {...rest}>
      {(title || subTitle) && (
        <CarouselHeader
          title={title}
          subTitle={subTitle}
          href={isGated ? undefined : headerLinkHref}
          as={headerLinkAs}
          prefetch={false}
          isGated={isGated}
          viewAllOnClick={handleViewAllClick}
        />
      )}

      {!isClient && promotionType === SEGMENT_PROMOTION_TYPE.imageBanners ? (
        <Flex
          mx={`calc(${theme.space._8 || 8} * -1px)`}
          gap={theme.space.space24}
        >
          {Array.from(
            Array(slidesDesktop || slidesTablet || slidesMobile || 3).keys()
          ).map((_, index) => (
            <Box key={index} px={1} height="325px" mx={1} width="100%">
              <Skeleton />
            </Box>
          ))}
        </Flex>
      ) : null}

      {isClient ? (
        <Carousel
          shouldReset={shouldReset}
          slideShadowSpace={slideShadowSpace}
          isProductCarousel={false}
          slidesDesktop={slidesDesktop}
          slidesTablet={slidesTablet}
          slidesMobile={slidesMobile}
          marginSize={isMobile ? "m" : "l"}
          imageElementWrapperHeight={imageElementWrapperHeight}
          hasVerticalPadding={hasVerticalPadding}
          items={brandsData.map((brand, index) => {
            const { id } = brand as SummaryBrand | HomePageSingleBrand;
            const brandData = brand as ImageBannerData;

            return (
              <SegmentBrandTracking
                brand={brand}
                key={id || brandData?.block_uuid}
                position={getPosition(brand, index)}
                tab={tab}
                promotionType={promotionType}
              >
                {({ onClickHandler }: BrandInjectedProps) => (
                  <BrandCard
                    associatedCategoryId={associatedCategoryId}
                    brand={brand}
                    genderId={genderId}
                    getImageElementWrapper={getImageElementWrapper}
                    locale={locale}
                    orderByNew={orderByNew}
                    onClickHandler={onClickHandler}
                    disableTranslation={disableTranslation}
                    brandType={promotionType}
                    refetchQueriesOnDeleteWishListBrand={[
                      "wishlistBrands",
                      "userWishlistMetaData",
                    ]}
                    position={getPosition(brand, index)}
                    tab={tab}
                    handleTimerExpired={handleTimerExpired}
                    pageName={pageName}
                    shopType={shopType}
                    setHasTimerEnded={setHasTimerEnded}
                  />
                )}
              </SegmentBrandTracking>
            );
          })}
        />
      ) : null}

      {open && isClient ? (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={headerLinkHref}
          trackingData={{
            pageName: pageName || "",
            shopType: shopType || genderId,
            actionSource: GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
            redirectTo: headerLinkHref,
          }}
          onRedirect={setRedirectData}
        />
      ) : null}
    </Box>
  );
};

export default BrandCarousel;
